import { MessageCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import { WhatsappLink } from '@virtual-care/ui';

import { useSupportPhone } from '../hooks/use-settings';
import { useLanguage, useTranslation } from '../i18n/hooks';

export default function Footer() {
  const location = useLocation();
  const t = useTranslation();
  const { direction } = useLanguage();
  const supportPhone = useSupportPhone();
  const whatsappDisplay = formatSaudiPhone(supportPhone);

  const navItems = [
    { title: t.nav.aboutUs, id: 'about', isSection: true },
    { title: t.nav.services, id: 'services', isSection: true },
    { title: t.nav.specialties, id: 'specialties', isSection: true },
    { title: t.nav.doctors, id: '/our-doctors', isSection: false },
    { title: t.nav.faq, id: 'faq', isSection: true },
  ];

  return (
    <footer className="bg-[#21397E] px-4 py-12 sm:px-6 md:px-8 lg:px-[120px]">
      <div className="mx-auto max-w-[1440px]">
        {/* Main Content */}
        <div className="grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-16">
          {/* Column 1: Logo & Tagline */}
          <div className="flex flex-col items-center gap-4 md:items-start">
            <Link
              to="/"
              className="rounded-2xl bg-white/20 p-4 transition-opacity hover:opacity-80"
            >
              {direction === 'rtl' ? (
                <img
                  src="/images/takassusi-consult-02.png"
                  alt="Virtual Care Logo"
                  className="h-28 w-28 object-contain"
                />
              ) : (
                <img
                  src="/images/takassusi-consult-01.png"
                  alt="Virtual Care Logo"
                  className="h-28 w-28 object-contain"
                />
              )}
            </Link>
            <p className="text-center text-lg font-medium text-white/95 md:text-start">
              {t.footer.tagline.split('\n')[0]}
              <br />
              {t.footer.tagline.split('\n')[1]}
            </p>
          </div>

          {/* Column 2: Navigation */}
          <div className="flex flex-col items-center gap-6 md:items-start">
            <h3 className="text-xl font-bold text-white">
              {t.footer.quickLinks}
            </h3>
            <nav className="flex flex-col items-center gap-3 md:items-start">
              {navItems.map((item) => {
                const isActive =
                  !item.isSection && location.pathname === item.id;

                if (item.isSection) {
                  const href =
                    location.pathname === '/' ? `#${item.id}` : `/#${item.id}`;

                  return (
                    <a
                      key={item.id}
                      href={href}
                      className="text-base font-medium text-white/90 transition-colors hover:text-white hover:underline"
                    >
                      {item.title}
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.id}
                    to={item.id}
                    className={`text-base font-medium transition-colors hover:underline ${
                      isActive
                        ? 'text-white underline'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    {item.title}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Column 3: Contact */}
          <div className="flex flex-col items-center gap-6 md:items-start">
            <h3 className="text-xl font-bold text-white">
              {t.footer.contactUs}
            </h3>
            <div className="flex flex-col gap-4">
              {/* Email */}
              <a
                href="mailto:info@tgl.com.sa"
                className="flex items-center gap-3 text-white/90 transition-colors hover:text-white"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                  <img
                    src="/images/icons/email.svg"
                    alt="Email"
                    className="h-5 w-5 brightness-0 invert"
                  />
                </div>
                <span className="text-base font-medium">info@tgl.com.sa</span>
              </a>

              {/* WhatsApp */}
              <WhatsappLink
                phone={supportPhone}
                className="flex items-center gap-3 text-white/90 transition-colors hover:text-white"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <span dir="ltr" className="text-base font-medium">
                  {whatsappDisplay}
                </span>
              </WhatsappLink>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-8 h-px w-full bg-white/20" />

        {/* Bottom Section */}
        <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
          {/* Copyright */}
          <p dir="ltr" className="text-center text-sm text-white/80">
            {t.footer.copyright.replace(
              '{year}',
              new Date().getFullYear().toString(),
            )}
          </p>

          {/* Legal Link */}
          <Link
            to="/terms-and-privacy"
            className="text-base font-medium text-white/90 transition-colors hover:text-white hover:underline"
          >
            {t.footer.termsAndPrivacy}
          </Link>
        </div>
      </div>
    </footer>
  );
}

function formatSaudiPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('966')) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return raw;
}
